import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, User, RefreshCw } from 'lucide-react';
import { attendanceApi, CalendarDay } from '../../services/api/attendance';
import { mockCalendarData } from '../../services/mockData';
import { useAttendanceContext, AttendanceProvider } from '../../contexts/AttendanceContext';
import toast from 'react-hot-toast';

// Calendar day status types
type CalendarDayStatus = 'present' | 'absent' | 'leave' | 'holiday' | 'weekend' | 'partial' | 'future';

interface AttendanceCalendarProps {
  userId?: string;
  showLegend?: boolean;
  compact?: boolean;
  height?: number;
  showUserSelector?: boolean;
}

// Internal component that uses the context
const AttendanceCalendarContent: React.FC<AttendanceCalendarProps> = ({
  userId,
  showLegend = true,
  compact = false,
  height,
  showUserSelector = false,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  // Get context for refresh triggers
  const { refreshTrigger } = useAttendanceContext();

  // Get calendar data for the current month
  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      console.log('🔄 Fetching calendar data for:', {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        userId
      });

      const response = await attendanceApi.getAttendanceCalendar({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        userId,
        includeWeekends: true,
      });

      console.log('✅ Calendar API response:', response);

      // Check if response has the expected structure
      if (!response || !response.calendar) {
        console.warn('⚠️ Invalid calendar response structure:', response);
        throw new Error('Invalid response structure');
      }

      // Use the API response directly as it already matches the CalendarDay interface
      const transformedData = response.calendar;



      setCalendarData(transformedData || []);
    } catch (error: any) {
      console.error('Error fetching calendar data:', error);

      // More specific error handling
      if (error.response?.status === 401) {
        toast.error('Please log in to view attendance data');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view this data');
      } else if (error.response?.status === 404) {
        toast.error('Attendance data not found');
      } else {
        toast.error('Failed to load calendar data. Using sample data for demonstration.');
      }

      // Use mock data as fallback
      console.log('Using mock calendar data as fallback');

      // Use mock data directly as it now matches the CalendarDay interface
      setCalendarData(mockCalendarData.calendar || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('📅 Calendar refresh triggered:', { currentDate: currentDate.toDateString(), userId, refreshTrigger });
    fetchCalendarData();
  }, [currentDate, userId, refreshTrigger]); // Add refreshTrigger to dependencies

  // Navigate months
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Get status color classes
  const getStatusColor = (status: CalendarDayStatus) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'leave':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'holiday':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'weekend':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'future':
        return 'bg-gray-50 text-gray-500 border-gray-100';
      default:
        return 'bg-white text-gray-900 border-gray-200';
    }
  };

  // Get status display text
  const getStatusDisplay = (day: CalendarDay) => {
    switch (day.status) {
      case 'present':
        return day.attendance?.totalMinutes ? `${(day.attendance.totalMinutes / 60).toFixed(1)}h` : 'P';
      case 'absent':
        return 'AB';
      case 'leave':
        return 'L';
      case 'holiday':
        return 'H';
      case 'weekend':
        return 'WE';
      case 'partial':
        return 'P*';
      case 'future':
        return '';
      default:
        return day.displayText || '';
    }
  };

  // Generate calendar grid
  const generateCalendarGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

    const days = [];
    const current = new Date(startDate);

    // Generate 6 weeks (42 days) to fill the calendar grid
    for (let i = 0; i < 42; i++) {
      // Use timezone-safe date formatting to avoid UTC conversion issues
      const currentDateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      const dayData = calendarData.find(
        (d) => {
          // Handle both date string formats: "2025-09-18" and "2025-09-18T00:00:00.000Z"
          const dataDateStr = typeof d.date === 'string'
            ? d.date.split('T')[0]
            : new Date(d.date).toISOString().split('T')[0];
          const matches = dataDateStr === currentDateStr;



          return matches;
        }
      );

      days.push({
        date: new Date(current),
        isCurrentMonth: current.getMonth() === month,
        isToday: current.toDateString() === new Date().toDateString(),
        data: dayData,
      });

      // Debug logging for first few days of September
      if (i < 10 && current.getMonth() === 8) { // September = month 8
        console.log(`📅 Calendar Grid Day ${i}:`, {
          gridDate: currentDateStr,
          actualDate: current.toDateString(),
          dayOfWeek: current.getDay(),
          dayName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][current.getDay()],
          hasData: !!dayData,
          dataStatus: dayData?.status,
          dataIsWorkingDay: dayData?.isWorkingDay
        });
      }

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const calendarGrid = generateCalendarGrid();



  const monthYear = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

  // Debug: Log current month being displayed
  console.log('📅 Current month being displayed:', {
    currentDate: currentDate.toISOString().split('T')[0],
    monthYear,
    month: currentDate.getMonth(),
    year: currentDate.getFullYear()
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5" />
              <span>Attendance Calendar</span>
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateMonth('prev')}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium min-w-[120px] text-center">
                {monthYear}
              </span>
              <button
                onClick={() => navigateMonth('next')}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <div className="border-l border-gray-300 pl-2 ml-2">
                <button
                  onClick={fetchCalendarData}
                  disabled={loading}
                  className="p-2 hover:bg-gray-100 rounded-md disabled:opacity-50 text-gray-600"
                  title="Refresh calendar data"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading calendar...</span>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-medium text-gray-600 border-b border-gray-200"
                >
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {calendarGrid.map((day, index) => {
                // Get background styling based on day status
                const getBackgroundStyle = () => {
                  if (!day.data) return 'hover:bg-gray-50';

                  switch (day.data.status) {
                    case 'holiday':
                      return 'bg-red-50 hover:bg-red-100';
                    case 'weekend':
                      return 'bg-gray-100 hover:bg-gray-200';
                    default:
                      return 'hover:bg-gray-50';
                  }
                };

                return (
                  <div
                    key={index}
                    className={`
                      relative p-2 ${compact ? 'min-h-[60px]' : 'min-h-[90px]'} border border-gray-100 cursor-pointer flex flex-col
                      ${getBackgroundStyle()}
                      ${!day.isCurrentMonth ? 'opacity-30' : ''}
                      ${day.isToday ? 'ring-2 ring-blue-500' : ''}
                    `}
                    onClick={() => day.data && setSelectedDay(day.data)}
                  >
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {day.date.getDate()}
                  </div>

                  {/* Punch times display */}
                  {!compact && day.data && day.data.attendance && (day.data.status === 'present' || day.data.status === 'partial') && (
                    <div className="flex-1 flex flex-col justify-center space-y-0.5">
                      {day.data.attendance.clockIns.length > 0 && (
                        <div className="text-xs text-green-700 font-medium">
                          In: {new Date(day.data.attendance.clockIns[0]).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </div>
                      )}
                      {day.data.attendance.clockOuts.length > 0 && (
                        <div className="text-xs text-red-700 font-medium">
                          Out: {new Date(day.data.attendance.clockOuts[day.data.attendance.clockOuts.length - 1]).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Compact mode: show times in a single line */}
                  {compact && day.data && day.data.attendance && (day.data.status === 'present' || day.data.status === 'partial') && (
                    <div className="flex-1 flex justify-center items-center">
                      <div className="text-xs text-gray-700 font-medium text-center">
                        {day.data.attendance.clockIns.length > 0 && (
                          <span className="text-green-700">
                            {new Date(day.data.attendance.clockIns[0]).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}
                          </span>
                        )}
                        {day.data.attendance.clockIns.length > 0 && day.data.attendance.clockOuts.length > 0 && (
                          <span className="mx-1">-</span>
                        )}
                        {day.data.attendance.clockOuts.length > 0 && (
                          <span className="text-red-700">
                            {new Date(day.data.attendance.clockOuts[day.data.attendance.clockOuts.length - 1]).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Status badge */}
                  {day.data && (
                    <div className={`
                      absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-medium border
                      ${getStatusColor(day.data.status)}
                    `}>
                      {getStatusDisplay(day.data)}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Legend</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { status: 'present' as CalendarDayStatus, label: 'Present', display: 'P' },
                { status: 'absent' as CalendarDayStatus, label: 'Absent', display: 'AB' },
                { status: 'leave' as CalendarDayStatus, label: 'Leave', display: 'L' },
                { status: 'holiday' as CalendarDayStatus, label: 'Holiday', display: 'H' },
                { status: 'weekend' as CalendarDayStatus, label: 'Weekend', display: 'WE' },
                { status: 'partial' as CalendarDayStatus, label: 'Partial', display: 'P*' },
                { status: 'future' as CalendarDayStatus, label: 'Future', display: '' },
              ].map((item) => (
                <div key={item.status} className="flex items-center space-x-2">
                  <div className={`
                    px-2 py-1 rounded text-xs font-medium border
                    ${getStatusColor(item.status)}
                  `}>
                    {item.display}
                  </div>
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Selected Day Details */}
      {selectedDay && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">
              {new Date(selectedDay.date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </h4>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Status:</span>
              <span className={`
                px-2 py-1 rounded text-xs font-medium border
                ${getStatusColor(selectedDay.status)}
              `}>
                {selectedDay.status.charAt(0).toUpperCase() + selectedDay.status.slice(1)}
              </span>
            </div>
            
            {selectedDay.attendance && (
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  {selectedDay.attendance.clockIns.length > 0 && (
                    <>
                      <span className="font-medium">In:</span> {new Date(selectedDay.attendance.clockIns[0]).toLocaleTimeString()}
                      {selectedDay.attendance.clockOuts.length > 0 && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="font-medium">Out:</span> {new Date(selectedDay.attendance.clockOuts[selectedDay.attendance.clockOuts.length - 1]).toLocaleTimeString()}
                        </>
                      )}
                    </>
                  )}
                </span>
              </div>
            )}

            {selectedDay.attendance?.totalMinutes && (
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  <span className="font-medium">Total Hours:</span> {(selectedDay.attendance.totalMinutes / 60).toFixed(2)}h
                </span>
              </div>
            )}

            {(selectedDay.leave?.reason || selectedDay.holiday?.name) && (
              <div className="text-sm">
                <span className="font-medium">Notes:</span> {selectedDay.leave?.reason || selectedDay.holiday?.name}
              </div>
            )}
            
            <button
              onClick={() => setSelectedDay(null)}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main component - now uses the context provided at layout level
const AttendanceCalendar: React.FC<AttendanceCalendarProps> = (props) => {
  return <AttendanceCalendarContent {...props} />;
};

export default AttendanceCalendar;
